import React from 'react';
import { MagnifyingGlass, SlidersHorizontal, SquaresFour, Rows, X } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';

/**
 * One search + filters + sort + view toolbar shared by every admin operations page.
 * Filters are declarative: [{ key, label, options: [{value,label}] }]
 */
export const AdminToolbar = ({
  search,
  onSearch,
  searchPlaceholder = 'Search…',
  filters = [],
  values = {},
  onFilter,
  onClear,
  sortOptions = [],
  sort,
  onSort,
  view,
  onView,
  resultLabel,
}) => {
  const activeCount = filters.filter((f) => values[f.key] && values[f.key] !== 'all').length;

  return (
    <div className="space-y-3" data-testid="admin-toolbar">
      <div className="flex flex-col lg:flex-row lg:items-center gap-2.5">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full h-[35px] pl-9 pr-3 rounded-full bg-white border border-black/[0.08] text-[13px] outline-none focus:border-primary/50 transition-colors"
            data-testid="admin-toolbar-search"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {filters.map((filter) => (
            <Select
              key={filter.key}
              value={values[filter.key] || 'all'}
              onValueChange={(v) => onFilter(filter.key, v)}
            >
              <SelectTrigger
                className={cn(
                  'h-[35px] rounded-full text-[13px] bg-white border-black/[0.08] w-auto min-w-[130px] px-3.5',
                  values[filter.key] && values[filter.key] !== 'all' && 'border-primary/50 text-primary'
                )}
                data-testid={`admin-filter-${filter.key}`}
              >
                <SelectValue placeholder={filter.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{filter.label}: all</SelectItem>
                {filter.options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}

          {activeCount > 0 && (
            <button
              onClick={onClear}
              className="inline-flex items-center gap-1 h-[29px] px-3 rounded-full text-[13px] text-foreground-muted hover:text-slate-900"
              data-testid="admin-toolbar-clear"
            >
              <X size={12} weight="bold" /> Clear {activeCount}
            </button>
          )}

          {sortOptions.length > 0 && (
            <Select value={sort} onValueChange={onSort}>
              <SelectTrigger
                className="h-[35px] rounded-full text-[13px] bg-white border-black/[0.08] w-auto min-w-[140px] px-3.5"
                data-testid="admin-toolbar-sort"
              >
                <SlidersHorizontal size={13} className="mr-1.5 text-foreground-muted" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {onView && (
            <div className="inline-flex items-center rounded-full border border-black/[0.08] bg-white p-0.5" data-testid="admin-toolbar-view">
              {[
                { key: 'card', Icon: SquaresFour },
                { key: 'list', Icon: Rows },
              ].map(({ key, Icon }) => (
                <button
                  key={key}
                  onClick={() => onView(key)}
                  className={cn(
                    'h-[29px] w-8 rounded-full inline-flex items-center justify-center transition-colors',
                    view === key ? 'bg-slate-900 text-white' : 'text-foreground-muted hover:text-slate-900'
                  )}
                  data-testid={`admin-view-${key}`}
                >
                  <Icon size={14} weight={view === key ? 'fill' : 'regular'} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {resultLabel && (
        <p className="text-xs text-foreground-muted" data-testid="admin-toolbar-count">{resultLabel}</p>
      )}
    </div>
  );
};

export default AdminToolbar;
